import { apiClient } from '@api/apiClient';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast';
import {
  ConsolidatedFeatureFlagAndOverrideModel,
  FeatureFlagOverride,
  FeatureFlag as FeatureFlagType,
  OfficeAndUserFeatureFlagsModel,
} from '@typeDefinitions/featureFlagTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

import { Feature } from './types';

export const GetFeatureFlags = async (): Promise<Feature[]> => {
  const result = await apiClient.get('FeatureFlag/GetFeatureFlags');

  if ('value' in result) {
    return result.value as Feature[];
  }
  return [] as Feature[];
};

export const GetAllFeatureFlags = async (): Promise<FeatureFlagType[]> => {
  try {
    return (await apiClient.get<FeatureFlagType[]>(
      'SelfService/getAllFeatureFlags',
    )) as unknown as FeatureFlagType[];
  } catch (e) {
    logError('GetFeatureFlags', e as Error);
    return [] as FeatureFlagType[];
  }
};

export const GetAllFeatureFlagOverrides = async (
  id: string,
): Promise<FeatureFlagOverride[]> => {
  try {
    return (await apiClient.get<FeatureFlagOverride[]>(
      'SelfService/getFeatureFlagOverrides/' + id,
    )) as unknown as FeatureFlagOverride[];
  } catch (e) {
    logError('GetFeatureFlags', e as Error);
    return [] as FeatureFlagOverride[];
  }
};

export const updateFeatureFlag = async (
  featureflag: FeatureFlagType,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<void> => {
  try {
    await apiClient.put(
      'SelfService/UpdateFeatureFlag',
      featureflag,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    handleToast(ToastType.Success, 'Feature Flag Updated Successfully');
  } catch (e) {
    logError('updateFeatureFlag', e as Error);
    throw createError(e, 'Failed to update feature flag');
  }
};

export const addFeatureFlagOverride = async (
  featureflag: FeatureFlagOverride,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<void> => {
  try {
    await apiClient.post(
      'SelfService/AddFeatureFlagOverride',
      featureflag,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    handleToast(ToastType.Success, 'Feature Flag Override Added Successfully');
  } catch (e) {
    throw createError(e, 'Failed to add feature flag override');
  }
};

export const deleteFeatureFlagOverride = async (
  id: string,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<void> => {
  try {
    await apiClient.delete(
      'SelfService/DeleteFeatureFlagOverride/' + id,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    handleToast(
      ToastType.Success,
      'Feature Flag Override Deleted Successfully',
    );
  } catch (e) {
    throw createError(e, 'Failed to delete feature flag override');
  }
};

export const updateFeatureFlagOverride = async (
  featureflag: FeatureFlagOverride,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<void> => {
  try {
    await apiClient.put(
      'SelfService/UpdateFeatureFlagOverride',
      featureflag,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    handleToast(
      ToastType.Success,
      'Feature Flag Override Updated Successfully',
    );
  } catch (e) {
    throw createError(e, 'Failed to update feature flag override');
  }
};

export const getAllFeatureFlagsForOffice = async (
  id: string,
): Promise<OfficeAndUserFeatureFlagsModel[]> => {
  try {
    const response = await apiClient.get(
      'SelfService/getFeatureFlagsForOffice/' + id,
    );
    return response as unknown as OfficeAndUserFeatureFlagsModel[];
  } catch (e) {
    throw createError(e, 'Failed to get feature flags for office');
  }
};

export const getAllUserFeatureFlags = async (
  id: string,
): Promise<ConsolidatedFeatureFlagAndOverrideModel[]> => {
  try {
    const response = await apiClient.get(
      'SelfService/getUserFeatureFlags/' + id,
    );
    return response as unknown as ConsolidatedFeatureFlagAndOverrideModel[];
  } catch (e) {
    throw createError(e, 'Failed to get user feature flags');
  }
};

export const getAllCustomerFeatureFlags = async (
  id: string,
): Promise<ConsolidatedFeatureFlagAndOverrideModel[]> => {
  try {
    const response = await apiClient.get(
      'SelfService/getCustomerFeatureFlags/' + id,
    );
    return response as unknown as ConsolidatedFeatureFlagAndOverrideModel[];
  } catch (e) {
    throw createError(e, 'Failed to get customer feature flags');
  }
};

export const getAllCarrierFeatureFlags = async (
  id: string,
): Promise<ConsolidatedFeatureFlagAndOverrideModel[]> => {
  try {
    const response = await apiClient.get(
      'SelfService/getCarrierFeatureFlags/' + id,
    );
    return response as unknown as ConsolidatedFeatureFlagAndOverrideModel[];
  } catch (e) {
    throw createError(e, 'Failed to get carrier feature flags');
  }
};

export const getFeatureFlagOverride = async (
  id: string,
): Promise<FeatureFlagOverride> => {
  try {
    const response = await apiClient.get(
      'SelfService/getFeatureFlagOverride/' + id,
    );
    return response as unknown as FeatureFlagOverride;
  } catch (e) {
    throw createError(e, 'Failed to get feature flag override');
  }
};
